import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Password requirement item component
const RequirementItem = ({ met, text }) => (
  <div className={`flex items-center gap-2 text-sm ${met ? 'text-[#2E7D32]' : 'text-gray-400'}`}>
    {met ? <Check size={16} className="text-[#2E7D32]" /> : <X size={16} className="text-gray-300" />}
    <span>{text}</span>
  </div>
);

export default function ForcePasswordUpdate({ onSuccess, onLogout }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password validation checks
  const hasMinLength = newPassword.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const isValid = hasMinLength && hasLetter && hasNumber && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      // Get token from the correct localStorage key
      const token = localStorage.getItem('yorm_token');
      
      if (!token) {
        toast.error('Session expired. Please log in again.');
        onLogout();
        return;
      }
      
      await axios.post(`${API}/api/auth/force-password-update?token=${token}`, {
        new_password: newPassword
      });
      
      toast.success('Password updated successfully! Please log in with your new password.');
      
      // Clear storage and redirect to login
      localStorage.removeItem('yorm_token');
      onLogout();
      
    } catch (error) {
      console.error('Password update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('yorm_token');
    onLogout();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1250] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="force-password-update-modal">
        {/* Header with Logo */}
        <div className="bg-white px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex flex-col items-center text-center">
            {/* Yorm Logo */}
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_e194f4c5-8d32-4ac0-8243-34dcb9b16c4c/artifacts/i9igc7w1_FBABBFB0-B86A-40AF-A319-0D7428AF7412.png" 
                alt="Yorm" 
                className="h-12 w-auto"
              />
            </div>
            
            <h2 className="text-xl font-bold text-[#222222]">Password Update Required</h2>
            <p className="text-gray-500 text-sm mt-1">
              Your password needs to be updated to meet our new security requirements
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 bg-white">
          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-[#222222] mb-1.5">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4A853] focus:border-[#D4A853] transition bg-white"
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-[#222222] mb-1.5">
              Confirm New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4A853] focus:border-[#D4A853] transition bg-white"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Requirements Checklist */}
          <div className="bg-[#F9F9F9] rounded-lg p-4 space-y-2.5">
            <p className="text-sm font-medium text-[#222222] mb-2">Password Requirements:</p>
            <RequirementItem met={hasMinLength} text="At least 8 characters" />
            <RequirementItem met={hasLetter} text="At least one letter (a-z, A-Z)" />
            <RequirementItem met={hasNumber} text="At least one number (0-9)" />
            <RequirementItem met={passwordsMatch} text="Passwords match" />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleLogout}
              className="flex-1 py-3 px-4 border border-gray-300 text-[#222222] rounded-lg hover:bg-gray-50 transition font-medium"
              data-testid="logout-btn"
            >
              Log Out
            </button>
            <button
              type="submit"
              disabled={!isValid || loading}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                isValid && !loading
                  ? 'bg-[#D4A853] hover:bg-[#C49843] text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="update-password-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Lock size={18} />
                  Update Password
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
